/**
 * tdesign v1.0.6
 * (c) 2023 TDesign Group
 * @license MIT
 */

import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { defineComponent, getCurrentInstance, toRefs, ref, computed, onMounted, watch, resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, toDisplayString, createCommentVNode, createVNode, Fragment, renderList } from 'vue';
import isString from 'lodash/isString';
import isBoolean from 'lodash/isBoolean';
import config from '../config.js';
import PickerProps from './props.js';
import '../shared/index.js';
import script$1 from './picker-item.js';
import TNodeComponent from '../shared/render-tnode.js';
import { useVModel } from '../shared/useVModel/index.js';
import { renderTNode } from '../shared/render.js';
import { useChildSlots } from '../shared/useChildSlots/index.js';
import '../shared/functions.js';
import '../shared/util.js';
import 'lodash/isNumber';
import '../shared/component.js';
import '../shared/constants.js';
import '../shared/useToggle/index.js';
import '../shared/useCountDown/index.js';
import '@babel/runtime/helpers/asyncToGenerator';
import '@babel/runtime/regenerator';
import '@vueuse/core';
import '../shared/useCountDown/utils.js';
import '../shared/useDefault/index.js';
import 'lodash/camelCase';
import '../shared/useTouch/index.js';
import '../shared/useScrollParent/index.js';
import '../shared/useExpose/index.js';
import '../shared/useTest/index.js';
import '@babel/runtime/helpers/defineProperty';
import '../shared/useClickAway/index.js';
import '@babel/runtime/helpers/toConsumableArray';
import 'lodash/isArray';
import '../shared/useGesture/index.js';
import '@use-gesture/vanilla';
import '../shared/hover.js';
import '@babel/runtime/helpers/typeof';
import 'lodash/kebabCase';
import './picker.class.js';
import '@babel/runtime/helpers/classCallCheck';
import '@babel/runtime/helpers/createClass';
import '../shared/dom.js';
import 'lodash/isFunction';

var prefix = config.prefix;
var name = "".concat(prefix, "-picker");
var getIndexFromColumns = function getIndexFromColumns(column, value) {
  if (!value) return 0;
  return column === null || column === void 0 ? void 0 : column.findIndex(function (item) {
    return item.value === value;
  });
};
var script = defineComponent({
  name: name,
  components: {
    PickerItem: script$1,
    TNode: TNodeComponent
  },
  props: PickerProps,
  emits: ["change", "cancel", "pick", "update:modelValue", "update:value"],
  setup: function setup(props) {
    var _pickerValue$value;
    var internalInstance = getCurrentInstance();
    var _toRefs = toRefs(props),
      value = _toRefs.value,
      modelValue = _toRefs.modelValue;
    var _useVModel = useVModel(value, modelValue, props.defaultValue, props.onChange),
      _useVModel2 = _slicedToArray(_useVModel, 2),
      _useVModel2$ = _useVModel2[0],
      pickerValue = _useVModel2$ === void 0 ? ref([]) : _useVModel2$,
      setPickerValue = _useVModel2[1];
    var getDefaultText = function getDefaultText(prop, defaultText) {
      if (isString(prop)) return prop;
      if (isBoolean(prop) && prop) return defaultText;
      return "";
    };
    var confirmButtonText = computed(function () {
      return getDefaultText(props.confirmBtn, "\u786E\u8BA4");
    });
    var cancelButtonText = computed(function () {
      return getDefaultText(props.cancelBtn, "\u53D6\u6D88");
    });
    var header = computed(function () {
      return renderTNode(internalInstance, "header");
    });
    var curValueArray = ref(((_pickerValue$value = pickerValue.value) === null || _pickerValue$value === void 0 ? void 0 : _pickerValue$value.map(function (item) {
      return item;
    })) || []);
    var realColumns = computed(function () {
      if (typeof props.columns === "function") {
        return props.columns(curValueArray.value);
      }
      return props.columns;
    });
    var curIndexArray = realColumns.value.map(function (item, index) {
      var _pickerValue$value2;
      return getIndexFromColumns(item, (_pickerValue$value2 = pickerValue.value) === null || _pickerValue$value2 === void 0 ? void 0 : _pickerValue$value2[index]);
    });
    var pickerItemInstanceArray = ref([]);
    onMounted(function () {
      pickerItemInstanceArray.value = useChildSlots("t-picker-item").map(function (item) {
        return item.component;
      });
    });
    var handleConfirm = function handleConfirm(e) {
      var _props$onConfirm;
      var target = realColumns.value.map(function (item, index) {
        return item[curIndexArray[index]];
      });
      var label = target.map(function (item) {
        return item.label;
      });
      var value2 = target.map(function (item) {
        return item.value;
      });
      setPickerValue(value2);
      (_props$onConfirm = props.onConfirm) === null || _props$onConfirm === void 0 || _props$onConfirm.call(props, value2, {
        index: curIndexArray,
        label: label,
        e: e
      });
    };
    var handleCancel = function handleCancel(e) {
      var _props$onCancel;
      pickerItemInstanceArray.value.forEach(function (item, index) {
        var _item$exposed;
        (_item$exposed = item.exposed) === null || _item$exposed === void 0 || _item$exposed.setIndex(curIndexArray[index]);
      });
      (_props$onCancel = props.onCancel) === null || _props$onCancel === void 0 || _props$onCancel.call(props, {
        e: e
      });
    };
    var handlePick = function handlePick(context, column) {
      var _realColumns$value, _props$onPick;
      var index = context.index;
      curIndexArray[column] = index;
      curValueArray.value[column] = (_realColumns$value = realColumns.value) === null || _realColumns$value === void 0 || (_realColumns$value = _realColumns$value[column][index]) === null || _realColumns$value === void 0 ? void 0 : _realColumns$value.value;
      (_props$onPick = props.onPick) === null || _props$onPick === void 0 || _props$onPick.call(props, curValueArray.value, {
        index: index,
        column: column
      });
    };
    watch(pickerValue, function () {
      curValueArray.value = pickerValue.value.map(function (item) {
        return item;
      });
    });
    watch([realColumns, curValueArray], function () {
      realColumns.value.forEach(function (col, idx) {
        var _pickerItemInstanceAr;
        var index = col.findIndex(function (item) {
          return item.value === curValueArray.value[idx];
        });
        curIndexArray[idx] = index > -1 ? index : 0;
        (_pickerItemInstanceAr = pickerItemInstanceArray.value[idx].exposed) === null || _pickerItemInstanceAr === void 0 || _pickerItemInstanceAr.setIndex(curIndexArray[idx]);
      });
    });
    return {
      name: name,
      header: header,
      pickerValue: pickerValue,
      curIndexArray: curIndexArray,
      confirmButtonText: confirmButtonText,
      cancelButtonText: cancelButtonText,
      handleConfirm: handleConfirm,
      handleCancel: handleCancel,
      handlePick: handlePick,
      realColumns: realColumns
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_t_node = resolveComponent("t-node");
  var _component_picker_item = resolveComponent("picker-item");
  return openBlock(), createElementBlock("div", {
    class: normalizeClass("".concat(_ctx.name))
  }, [createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__toolbar"))
  }, [_ctx.cancelButtonText ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass("".concat(_ctx.name, "__cancel")),
    onClick: _cache[0] || (_cache[0] = function () {
      return _ctx.handleCancel && _ctx.handleCancel.apply(_ctx, arguments);
    })
  }, toDisplayString(_ctx.cancelButtonText), 3)) : createCommentVNode("", true), createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__title"))
  }, toDisplayString(_ctx.title), 3), _ctx.confirmButtonText ? (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass("".concat(_ctx.name, "__confirm")),
    onClick: _cache[1] || (_cache[1] = function () {
      return _ctx.handleConfirm && _ctx.handleConfirm.apply(_ctx, arguments);
    })
  }, toDisplayString(_ctx.confirmButtonText), 3)) : createCommentVNode("", true)], 2), createVNode(_component_t_node, {
    content: _ctx.header
  }, null, 8, ["content"]), createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__main"))
  }, [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.realColumns, function (item, index) {
    var _ctx$pickerValue;
    return openBlock(), createElementBlock("div", {
      key: index,
      class: normalizeClass("".concat(_ctx.name, "-item__group"))
    }, [createVNode(_component_picker_item, {
      options: item,
      value: (_ctx$pickerValue = _ctx.pickerValue) === null || _ctx$pickerValue === void 0 ? void 0 : _ctx$pickerValue[index],
      "render-label": _ctx.renderLabel,
      onPick: function onPick($event) {
        return _ctx.handlePick($event, index);
      }
    }, null, 8, ["options", "value", "render-label", "onPick"])], 2);
  }), 128)), createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__mask ").concat(_ctx.name, "__mask--top"))
  }, null, 2), createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__mask ").concat(_ctx.name, "__mask--bottom"))
  }, null, 2), createElementVNode("div", {
    class: normalizeClass("".concat(_ctx.name, "__indicator"))
  }, null, 2)], 2)], 2);
}

script.render = render;

export { script as default };
//# sourceMappingURL=picker.js.map
